import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, Typography, Row, Col, Image, Tooltip, Modal, List, Avatar } from 'antd'
import {
	ControlOutlined,
	ArrowRightOutlined,
	StarFilled,
	CrownOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { portalAccentGhostButtonStyle, useTheme, useThemeColors } from '../../common/ThemeContext'
import { useBranding } from '../../common/branding/useBranding'
import {
	WelcomeCustomizeDrawer,
	WelcomeModuleIcon,
	type WelcomeLayout,
	resolvedTileAsset,
} from './WelcomeCustomizeDrawer'
import { httpGet, httpPut } from '../../common/http.service'
import { attendanceService } from '../../common/service/AttendanceService'
import { leaveService } from '../../common/service/leave.service'
import { staffService } from '../../common/service/staff.service'
import logo from './../../Assets/img/logo-no-background.png'
import primersoftLogo from './../../Assets/img/primersoft/primersoft-logo.svg'
import { MODULE_DEFS, MODULE_ROUTES, broadcastWelcomeLayout, getAppSettingsPayload, mergeWelcomeLayoutFromServer, persistWelcomeLayout, persistWelcomeModuleIcons, readStoredWelcomeLayout, readStoredWelcomeModuleIcons } from './welcomeModules'

const { Title, Text } = Typography
const CAMBODIA_DATE_FMT = 'YYYY-MM-DD'

type OverviewCounts = {
	leaveToday: number
	lateToday: number
	tooLateToday: number
	leftEarlyYesterday: number
}

type StaffPerformance = {
	staffId: string
	fullName: string
	photoUrl?: string
	rating: number
	score: number
	onTimeCount: number
	earlyCount: number
	lateCheckoutCount: number
}

type OverviewStaffRow = {
	staffId: string
	fullName: string
	photoUrl?: string
	info: string
}

type AttendanceRecord = {
	staffId?: string
	fullName?: string
	checkInAt?: string | Date | null
	checkOutAt?: string | Date | null
	workingHours?: {
		start?: string | Date | null
		to?: string | Date | null
	}
}

const asDayjs = (value: string | Date | null | undefined) => {
	if (!value) return null
	const parsed = dayjs(value)
	return parsed.isValid() ? parsed : null
}

function metricCard(
	title: string,
	value: number,
	color: string,
	onClick: (() => void) | undefined,
	titleColor: string,
) {
	return (
		<Card
			size='small'
			onClick={onClick}
			style={{
				borderRadius: 12,
				borderColor: `${color}33`,
				cursor: onClick ? 'pointer' : 'default',
			}}
			hoverable={Boolean(onClick)}
		>
			<Text style={{ color: titleColor, fontSize: 12 }}>{title}</Text>
			<Title level={3} style={{ margin: '6px 0 0', color }}>
				{value}
			</Title>
		</Card>
	)
}

function SidebarAttendanceOverview({ primary, layout }: { primary: string; layout: WelcomeLayout }) {
	const { isDarkMode } = useTheme()
	const themeColors = useThemeColors()
	const metricTitleColor = isDarkMode ? themeColors.textSecondary : '#64748b'
	const [loading, setLoading] = useState(false)
	const [counts, setCounts] = useState<OverviewCounts>({
		leaveToday: 0,
		lateToday: 0,
		tooLateToday: 0,
		leftEarlyYesterday: 0,
	})
	const [topStaff, setTopStaff] = useState<StaffPerformance[]>([])
	const [detailRows, setDetailRows] = useState<Record<string, OverviewStaffRow[]>>({
		leaveToday: [],
		lateToday: [],
		tooLateToday: [],
		leftEarlyYesterday: [],
		topStaff: [],
	})
	const [activeDetailKey, setActiveDetailKey] = useState<keyof typeof detailRows | null>(null)

	useEffect(() => {
		if (layout !== 'sidebar') return

		let mounted = true
		const loadOverview = async () => {
			try {
				setLoading(true)
				const today = dayjs().startOf('day')
				const yesterday = today.subtract(1, 'day')
				const [
					todayAttendanceRes,
					yesterdayAttendanceRes,
					checkInLimitRes,
					leaveRes,
					topPerfRes,
					staffRes,
				] = await Promise.allSettled([
					attendanceService.fetchTodayListing(),
					attendanceService.fetchListing({
						start_date: yesterday.format('MM/DD/YYYY'),
						end_date: yesterday.format('MM/DD/YYYY'),
						limit: 500,
					}),
					httpGet('/api/v1/admin/config-shift/hour-limitation/checkin'),
					leaveService.fetchListing({ status: 'Approved', page: 1, limit: 500 }),
					attendanceService.fetchListing({
						start_date: today.subtract(30, 'day').format('MM/DD/YYYY'),
						end_date: today.format('MM/DD/YYYY'),
						limit: 500,
					}),
					staffService.fetchListing({ limit: 1000 }),
				])

				const settledValue = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
					result.status === 'fulfilled' ? result.value : fallback

				const tooLateLimit =
					settledValue(checkInLimitRes, null as any)?.data?.hourLateNotAllowInMinutes ??
					settledValue(checkInLimitRes, null as any)?.hourLateNotAllowInMinutes ??
					60

				const todayAttendancePayload = settledValue(todayAttendanceRes, {} as any)
				const yesterdayAttendancePayload = settledValue(yesterdayAttendanceRes, {} as any)
				const leavePayload = settledValue(leaveRes, {} as any)
				const topPerfPayload = settledValue(topPerfRes, {} as any)
				const staffPayload = settledValue(staffRes, {} as any)

				const todayRecords: AttendanceRecord[] = Array.isArray(todayAttendancePayload?.data)
					? todayAttendancePayload.data
					: []
				const yesterdayRecords: AttendanceRecord[] = Array.isArray(yesterdayAttendancePayload?.data)
					? yesterdayAttendancePayload.data
					: []
				const leaveItems = Array.isArray(leavePayload?.data?.data)
					? leavePayload.data.data
					: Array.isArray(leavePayload?.data)
						? leavePayload.data
						: []
				const topPerfRecords: AttendanceRecord[] = Array.isArray(topPerfPayload?.data)
					? topPerfPayload.data
					: []
				const staffItems = Array.isArray(staffPayload?.data) ? staffPayload.data : []
				const staffMap = new Map<
					string,
					{ fullName: string; photoUrl?: string }
				>(
					staffItems.map((s: any) => [
						String(s.id),
						{
							fullName:
								s.nameInEnglish ||
								`${s.lastName || ''} ${s.firstName || ''}`.trim() ||
								String(s.id),
							photoUrl: s.photoUrl || undefined,
						},
					]),
				)

				const lateTodayRows = todayRecords
					.filter((rec) => {
						const checkIn = asDayjs(rec.checkInAt)
						const start = asDayjs(rec.workingHours?.start)
						return Boolean(checkIn && start && checkIn.diff(start, 'minute') > 0)
					})
					.map((rec) => {
						const key = String(rec.staffId || '')
						const checkIn = asDayjs(rec.checkInAt)
						const start = asDayjs(rec.workingHours?.start)
						const lateMin = checkIn && start ? checkIn.diff(start, 'minute') : 0
						const staff = staffMap.get(key)
						return {
							staffId: key,
							fullName: staff?.fullName || rec.fullName || key,
							photoUrl: staff?.photoUrl,
							info: `Late by ${lateMin} min`,
						}
					})
				const lateToday = lateTodayRows.length

				const tooLateTodayRows = todayRecords
					.filter((rec) => {
						const checkIn = asDayjs(rec.checkInAt)
						const start = asDayjs(rec.workingHours?.start)
						return Boolean(checkIn && start && checkIn.diff(start, 'minute') >= tooLateLimit)
					})
					.map((rec) => {
						const key = String(rec.staffId || '')
						const checkIn = asDayjs(rec.checkInAt)
						const start = asDayjs(rec.workingHours?.start)
						const lateMin = checkIn && start ? checkIn.diff(start, 'minute') : 0
						const staff = staffMap.get(key)
						return {
							staffId: key,
							fullName: staff?.fullName || rec.fullName || key,
							photoUrl: staff?.photoUrl,
							info: `Too late by ${lateMin} min`,
						}
					})
				const tooLateToday = tooLateTodayRows.length

				const leftEarlyYesterdayRows = yesterdayRecords
					.filter((rec) => {
						const checkOut = asDayjs(rec.checkOutAt)
						const shiftEnd = asDayjs(rec.workingHours?.to)
						return Boolean(checkOut && shiftEnd && checkOut.isBefore(shiftEnd))
					})
					.map((rec) => {
						const key = String(rec.staffId || '')
						const checkOut = asDayjs(rec.checkOutAt)
						const shiftEnd = asDayjs(rec.workingHours?.to)
						const earlyMin = checkOut && shiftEnd ? shiftEnd.diff(checkOut, 'minute') : 0
						const staff = staffMap.get(key)
						return {
							staffId: key,
							fullName: staff?.fullName || rec.fullName || key,
							photoUrl: staff?.photoUrl,
							info: `Left early by ${earlyMin} min`,
						}
					})
				const leftEarlyYesterday = leftEarlyYesterdayRows.length

				const leaveTodayRows = leaveItems
					.filter((leave: any) => {
						const start = asDayjs(leave?.startDate)
						const end = asDayjs(leave?.endDate)
						if (!start || !end) return false
						const todayKey = today.format(CAMBODIA_DATE_FMT)
						return (
							todayKey >= start.startOf('day').format(CAMBODIA_DATE_FMT) &&
							todayKey <= end.startOf('day').format(CAMBODIA_DATE_FMT)
						)
					})
					.map((leave: any) => {
						const staffId = String(
							leave?.requester?.id || leave?.requesterId || leave?.requestorId || '',
						)
						const staff = staffMap.get(staffId)
						const start = asDayjs(leave?.startDate)?.format('YYYY-MM-DD') || '-'
						const end = asDayjs(leave?.endDate)?.format('YYYY-MM-DD') || '-'
						return {
							staffId,
							fullName:
								leave?.requester?.name ||
								leave?.requesterName ||
								staff?.fullName ||
								staffId,
							photoUrl: leave?.requester?.photoUrl || staff?.photoUrl,
							info: `${leave?.leaveType || 'Leave'} (${start} to ${end})`,
						}
					})
				const leaveToday = leaveTodayRows.length

				const dedupeRows = (rows: OverviewStaffRow[]) => {
					const m = new Map<string, OverviewStaffRow>()
					rows.forEach((r) => {
						if (!r.staffId) return
						if (!m.has(r.staffId)) m.set(r.staffId, r)
					})
					return Array.from(m.values())
				}

				const dedupedLateTodayRows = dedupeRows(lateTodayRows)
				const dedupedTooLateTodayRows = dedupeRows(tooLateTodayRows)
				const dedupedLeftEarlyYesterdayRows = dedupeRows(leftEarlyYesterdayRows)
				const dedupedLeaveTodayRows = dedupeRows(leaveTodayRows)

				const statsByStaff = new Map<
					string,
					{
						name: string
						onTime: number
						early: number
						lateCheckout: number
						late: number
						tooLate: number
						earlyLeave: number
					}
				>()

				topPerfRecords.forEach((rec) => {
					if (!rec.staffId) return
					const key = String(rec.staffId)
					const current = statsByStaff.get(key) ?? {
						name: rec.fullName || key,
						onTime: 0,
						early: 0,
						lateCheckout: 0,
						late: 0,
						tooLate: 0,
						earlyLeave: 0,
					}
					const checkIn = asDayjs(rec.checkInAt)
					const checkOut = asDayjs(rec.checkOutAt)
					const shiftStart = asDayjs(rec.workingHours?.start)
					const shiftEnd = asDayjs(rec.workingHours?.to)

					if (checkIn && shiftStart) {
						const diffIn = checkIn.diff(shiftStart, 'minute')
						if (diffIn <= 0) current.onTime += 1
						if (diffIn <= -10) current.early += 1
						if (diffIn > 0) current.late += 1
						if (diffIn >= tooLateLimit) current.tooLate += 1
					}

					if (checkOut && shiftEnd) {
						if (checkOut.isAfter(shiftEnd) || checkOut.isSame(shiftEnd)) current.lateCheckout += 1
						if (checkOut.isBefore(shiftEnd)) current.earlyLeave += 1
					}

					statsByStaff.set(key, current)
				})

				const ranking = Array.from(statsByStaff.entries())
					.map(([staffId, s]) => {
						const good = s.onTime + s.early + s.lateCheckout
						const bad = s.late + s.tooLate * 2 + s.earlyLeave
						const ratio = good / Math.max(1, good + bad)
						const rating = Math.max(1, Math.min(5, Math.round(ratio * 5)))
						const score = good * 2 - bad * 2
						return {
							staffId,
							fullName: staffMap.get(staffId)?.fullName || s.name,
							photoUrl: staffMap.get(staffId)?.photoUrl,
							rating,
							score,
							onTimeCount: s.onTime,
							earlyCount: s.early,
							lateCheckoutCount: s.lateCheckout,
						}
					})
					.sort((a, b) => {
						if (b.rating !== a.rating) return b.rating - a.rating
						if (b.score !== a.score) return b.score - a.score
						if (b.onTimeCount !== a.onTimeCount) return b.onTimeCount - a.onTimeCount
						return b.lateCheckoutCount - a.lateCheckoutCount
					})
					.slice(0, 5)

				if (!mounted) return
				setCounts({
					leaveToday,
					lateToday,
					tooLateToday,
					leftEarlyYesterday,
				})
				setTopStaff(ranking)
				setDetailRows({
					leaveToday: dedupedLeaveTodayRows,
					lateToday: dedupedLateTodayRows,
					tooLateToday: dedupedTooLateTodayRows,
					leftEarlyYesterday: dedupedLeftEarlyYesterdayRows,
					topStaff: ranking.map((s) => ({
						staffId: s.staffId,
						fullName: s.fullName,
						photoUrl: s.photoUrl,
						info: `${s.rating} star | On-time ${s.onTimeCount}, Early ${s.earlyCount}, Leave-late ${s.lateCheckoutCount}`,
					})),
				})
			} catch {
				// Keep welcome page stable even if overview APIs timeout.
				if (!mounted) return
				setCounts({
					leaveToday: 0,
					lateToday: 0,
					tooLateToday: 0,
					leftEarlyYesterday: 0,
				})
				setTopStaff([])
				setDetailRows({
					leaveToday: [],
					lateToday: [],
					tooLateToday: [],
					leftEarlyYesterday: [],
					topStaff: [],
				})
			} finally {
				if (mounted) setLoading(false)
			}
		}

		loadOverview()
		return () => {
			mounted = false
		}
	}, [layout])

	if (layout !== 'sidebar') return null

	return (
		<div style={{ margin: '12px 20px 0' }}>
			<Row gutter={[12, 12]}>
				<Col xs={24} sm={12} lg={6}>
					{metricCard(
						'Staff on Leave Today',
						counts.leaveToday,
						primary,
						() => setActiveDetailKey('leaveToday'),
						metricTitleColor,
					)}
				</Col>
				<Col xs={24} sm={12} lg={6}>
					{metricCard(
						'Late Today',
						counts.lateToday,
						'#d97706',
						() => setActiveDetailKey('lateToday'),
						metricTitleColor,
					)}
				</Col>
				<Col xs={24} sm={12} lg={6}>
					{metricCard(
						'Too Late Today',
						counts.tooLateToday,
						'#dc2626',
						() => setActiveDetailKey('tooLateToday'),
						metricTitleColor,
					)}
				</Col>
				<Col xs={24} sm={12} lg={6}>
					{metricCard(
						'Left Early Yesterday',
						counts.leftEarlyYesterday,
						'#7c3aed',
						() => setActiveDetailKey('leftEarlyYesterday'),
						metricTitleColor,
					)}
				</Col>
			</Row>

			<Card
				loading={loading}
				title={<span style={{ color: primary, fontWeight: 700 }}>Top 1–5 Star Staff Performance</span>}
				style={{ marginTop: 12, borderRadius: 12 }}
			>
				{topStaff.length === 0 ? (
					<Text type='secondary'>No attendance data available yet for ranking.</Text>
				) : (
					<div style={{ display: 'grid', gap: 10 }}>
						{topStaff.map((staff, idx) => (
							<div
								key={staff.staffId}
								onClick={() => setActiveDetailKey('topStaff')}
								style={{
									border: '1px solid #e2e8f0',
									borderRadius: 10,
									padding: '10px 12px',
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									gap: 12,
									cursor: 'pointer',
								}}
							>
								<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
									<div style={{ position: 'relative', width: 36, height: 36 }}>
										<Avatar src={staff.photoUrl} size={36}>
											{(staff.fullName || '?').charAt(0)}
										</Avatar>
										{idx === 0 ? (
											<span
												style={{
													position: 'absolute',
													top: -10,
													right: -10,
													width: 18,
													height: 18,
													borderRadius: '50%',
													background: '#f59e0b',
													color: '#fff',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
												}}
											>
												<CrownOutlined style={{ fontSize: 11 }} />
											</span>
										) : null}
									</div>
									<div>
									<Text strong>{idx + 1}. {staff.fullName}</Text>
									<div style={{ marginTop: 2 }}>
										<Text type='secondary' style={{ fontSize: 12 }}>
											On-time: {staff.onTimeCount} | Early: {staff.earlyCount} | Leave-late: {staff.lateCheckoutCount}
										</Text>
									</div>
									</div>
								</div>
								<div style={{ display: 'flex', gap: 2 }}>
									{Array.from({ length: 5 }).map((_, i) => (
										<StarFilled key={i} style={{ color: i < staff.rating ? '#f59e0b' : '#e5e7eb' }} />
									))}
								</div>
							</div>
						))}
					</div>
				)}
			</Card>
			<Modal
				open={Boolean(activeDetailKey)}
				onCancel={() => setActiveDetailKey(null)}
				footer={null}
				width={760}
				title={
					activeDetailKey === 'leaveToday'
						? 'Staff on Leave Today'
						: activeDetailKey === 'lateToday'
							? 'Late Staff Today'
							: activeDetailKey === 'tooLateToday'
								? 'Too Late Staff Today'
								: activeDetailKey === 'leftEarlyYesterday'
									? 'Left Early Yesterday'
									: 'Top 1–5 Star Staff Performance'
				}
			>
				<List
					dataSource={activeDetailKey ? detailRows[activeDetailKey] : []}
					locale={{ emptyText: 'No staff found for this dashboard.' }}
					renderItem={(item) => (
						<List.Item>
							<List.Item.Meta
								avatar={<Avatar src={item.photoUrl}>{(item.fullName || '?').charAt(0)}</Avatar>}
								title={`${item.fullName} (${item.staffId || '-'})`}
								description={item.info}
							/>
						</List.Item>
					)}
				/>
			</Modal>
		</div>
	)
}

function useWelcomeSettings() {
	const [moduleIcons, setModuleIcons] = useState<Record<string, WelcomeModuleIcon>>(readStoredWelcomeModuleIcons)
	const [layout, setLayoutState] = useState<WelcomeLayout>(readStoredWelcomeLayout)
	const [rawSettings, setRawSettings] = useState<Record<string, unknown>>({})

	useEffect(() => {
		persistWelcomeModuleIcons(moduleIcons)
	}, [moduleIcons])

	useEffect(() => {
		httpGet('/api/v1/admin/app-settings')
			.then((res: unknown) => {
				const data = getAppSettingsPayload(res)
				const nextIcons = (data.welcomeModuleIcons as Record<string, WelcomeModuleIcon>) ?? {}
				setModuleIcons(nextIcons)
				persistWelcomeModuleIcons(nextIcons)
				const next = mergeWelcomeLayoutFromServer(data.welcomeLayout)
				setLayoutState(next)
				persistWelcomeLayout(next)
				setRawSettings(data)
			})
			.catch(() => {})
	}, [])

	const saveLayout = async (next: WelcomeLayout) => {
		setLayoutState(next)
		persistWelcomeLayout(next)
		broadcastWelcomeLayout(next)
		try {
			await httpPut('/api/v1/admin/app-settings', {
				...rawSettings,
				welcomeLayout: next,
			})
			setRawSettings((prev) => ({ ...prev, welcomeLayout: next }))
		} catch {
			// Still on sidebar URL with localStorage; non-fatal
		}
	}

	return { moduleIcons, setModuleIcons, layout, saveLayout }
}

function CustomizeButton({ onClick }: { onClick: () => void }) {
	const { theme, isDarkMode } = useTheme()
	const { primary } = useThemeColors()
	const ghost = portalAccentGhostButtonStyle(theme, primary)
	return (
		<Tooltip title='Customize module icons' placement='left'>
			<Button
				shape='round'
				icon={<ControlOutlined />}
				onClick={onClick}
				style={{
					...ghost,
					position: 'fixed',
					bottom: 32,
					right: 32,
					zIndex: 1000,
					boxShadow: isDarkMode
						? '0 4px 16px rgba(0,0,0,0.35)'
						: '0 4px 16px rgba(0,0,0,0.12)',
					display: 'flex',
					alignItems: 'center',
					gap: 6,
					paddingLeft: 16,
					paddingRight: 16,
					height: 42,
					fontSize: 14,
				}}
			>
				Customize
			</Button>
		</Tooltip>
	)
}

const PS_NAV_ITEMS = MODULE_DEFS.map((m, i) => ({
	key: m.key,
	defaultIcon: m.defaultIcon,
	label: m.defaultLabel,
	description: [
		'Manage system administrators and access control',
		'Configure organization profile and structure',
		'Create and manage company departments',
		'Employee records, shifts and assignments',
		'Leave requests, balances and approvals',
		'Real-time staff attendance tracking',
		'Reports, history and attendance analytics',
		'Data handling and privacy guidelines',
	][i],
	route: MODULE_ROUTES[m.key],
	accentLevel: i % 2 === 0 ? 'primary' : 'charcoal',
}))

function PrimersoftWelcome() {
	const navigate = useNavigate()
	const { primary, charcoal } = useThemeColors()
	const [hovered, setHovered] = useState<number | null>(null)
	const [customizeOpen, setCustomizeOpen] = useState(false)
	const { brandingLogoUrl } = useBranding()
	const { moduleIcons, setModuleIcons, layout, saveLayout } = useWelcomeSettings()

	const hero = (
		<div
			style={{
				background: '#fff',
				borderRadius: 16,
				padding: '32px 40px',
				marginBottom: 28,
				display: 'flex',
				alignItems: 'center',
				gap: 28,
				borderBottom: `4px solid ${primary}`,
				boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
			}}
		>
			<div style={{ flexShrink: 0 }}>
				<div
					style={{
						borderRadius: 16,
						overflow: 'hidden',
						background: '#f9fafb',
						padding: 8,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Image
						src={brandingLogoUrl || primersoftLogo}
						fallback={primersoftLogo}
						alt='Branding logo'
						preview={false}
						style={{ width: 140, height: 'auto', objectFit: 'contain', borderRadius: 10 }}
					/>
				</div>
			</div>
			<div>
				<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
					<div
						style={{
							width: 4,
							height: 28,
							backgroundColor: primary,
							borderRadius: 2,
							flexShrink: 0,
						}}
					/>
					<Title
						level={2}
						style={{ margin: 0, color: '#414042', fontWeight: 700, fontSize: 26, letterSpacing: '-0.3px' }}
					>
						Attendance Management System
					</Title>
				</div>
				<Text style={{ color: '#6b7280', fontSize: 14, marginLeft: 14 }}>
					Primersoft — Streamline your workforce operations from one place
				</Text>
			</div>
		</div>
	)

	const tiles = (
		<>
			<div style={{ marginBottom: 16 }}>
				<Text style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
					Quick Access
				</Text>
			</div>
			<Row gutter={[20, 20]}>
				{PS_NAV_ITEMS.map((item, i) => {
					const isHovered = hovered === i
					const asset = resolvedTileAsset(MODULE_DEFS.find((m) => m.key === item.key)!, moduleIcons)
					const hasImageOverride = asset.type === 'img' && moduleIcons[item.key]?.type === 'image'
					const itemAccent = item.accentLevel === 'primary' ? primary : charcoal
					return (
						<Col key={i} xs={24} sm={12} md={8} lg={6}>
							<div
								onClick={() => navigate(item.route)}
								onMouseEnter={() => setHovered(i)}
								onMouseLeave={() => setHovered(null)}
								style={{
									background: '#fff',
									borderRadius: 12,
									padding: '20px 20px 18px',
									cursor: 'pointer',
									border: isHovered ? `2px solid ${itemAccent}` : '2px solid transparent',
									boxShadow: isHovered ? `0 8px 24px ${itemAccent}22` : '0 2px 8px rgba(0,0,0,0.06)',
									transition: 'border 0.18s, box-shadow 0.18s, transform 0.15s',
									transform: isHovered ? 'translateY(-3px)' : 'none',
									display: 'flex',
									flexDirection: 'column',
									gap: 12,
									height: '100%',
								}}
							>
								<div
									style={{
										width: 48,
										height: 48,
										borderRadius: 12,
										backgroundColor: hasImageOverride ? 'transparent' : `${itemAccent}14`,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										flexShrink: 0,
										overflow: 'hidden',
									}}
								>
									{asset.type === 'img' ? (
										<img src={asset.src} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
									) : (
										<asset.component style={{ fontSize: 22, color: itemAccent }} />
									)}
								</div>
								<div style={{ flex: 1 }}>
									<Text strong style={{ display: 'block', fontSize: 15, color: '#1f2937', marginBottom: 4, lineHeight: 1.3 }}>
										{item.label}
									</Text>
									<Text style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
										{item.description}
									</Text>
								</div>
								<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
									<ArrowRightOutlined style={{ fontSize: 13, color: isHovered ? itemAccent : '#d1d5db', transition: 'color 0.18s' }} />
								</div>
							</div>
						</Col>
					)
				})}
			</Row>
		</>
	)

	return (
		<div style={{ backgroundColor: '#f3f4f6', minHeight: '100vh', paddingBottom: 80 }}>
			<div style={{ padding: '28px 32px' }}>
				{hero}
				{layout !== 'sidebar' ? tiles : null}
			</div>
			<SidebarAttendanceOverview primary={primary} layout={layout} />

			{layout !== 'sidebar' ? (
				<>
					<CustomizeButton onClick={() => setCustomizeOpen(true)} />
					<WelcomeCustomizeDrawer
						open={customizeOpen}
						onClose={() => setCustomizeOpen(false)}
						modules={MODULE_DEFS}
						moduleIcons={moduleIcons}
						onIconsChange={setModuleIcons}
						layout={layout}
						onLayoutChange={saveLayout}
					/>
				</>
			) : null}
		</div>
	)
}

function EDCWelcome() {
	const navigate = useNavigate()
	const { primary } = useThemeColors()
	const [customizeOpen, setCustomizeOpen] = useState(false)
	const { brandingLogoUrl } = useBranding()
	const { moduleIcons, setModuleIcons, layout, saveLayout } = useWelcomeSettings()

	const navItems = MODULE_DEFS.map((mod) => ({
		mod,
		onClick: () => navigate(MODULE_ROUTES[mod.key] ?? '/'),
	}))

	const edcHeader = (
		<Card style={{ margin: '20px', marginBottom: '0px', textAlign: 'center', borderRadius: '8px' }}>
			<Row align='middle' justify='center' style={{ gap: '97px' }}>
				<Col>
					<div
						style={{
							width: 100,
							height: 100,
							borderRadius: 18,
							overflow: 'hidden',
							background: '#ffffff',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
							boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
						}}
					>
						<Image
							src={brandingLogoUrl || logo}
							fallback={logo}
							alt='Branding logo'
							preview={false}
							style={{ width: '100%', height: '100%', objectFit: 'contain' }}
						/>
					</div>
				</Col>
				<Col>
					<Typography.Title
						aria-level={2}
						style={{ color: primary, fontFamily: 'Times New Roman', margin: 0, fontSize: '43px' }}
					>
						Welcome to Attendance Management System
					</Typography.Title>
				</Col>
			</Row>
		</Card>
	)

	const edcTiles = (
		<Card style={{ margin: '10px 20px', padding: '10px' }}>
			<Row gutter={[20, 20]} justify='center'>
				{navItems.map(({ mod, onClick }, index) => {
					const asset = resolvedTileAsset(mod, moduleIcons)
					return (
						<Col
							key={index}
							xs={24}
							sm={11}
							md={7}
							lg={5}
							style={{
								textAlign: 'center',
								boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
								borderRadius: '8px',
								background: 'white',
								padding: '20px',
								cursor: 'pointer',
								transition: 'transform 0.2s, box-shadow 0.2s',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
							}}
							onClick={onClick}
							onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
							onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
						>
							<div
								style={{
									width: '100%',
									height: '100%',
									marginBottom: '5px',
									overflow: 'hidden',
									borderRadius: '8px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									minHeight: 120,
									backgroundColor: asset.type === 'icon' ? `${primary}14` : 'transparent',
								}}
							>
								{asset.type === 'img' ? (
									<img src={asset.src} alt={`${mod.defaultLabel} icon`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
								) : (
									<asset.component style={{ fontSize: 64, color: primary }} />
								)}
							</div>
							<button
								type='button'
									style={{
									width: '100%',
									height: '40px',
									backgroundColor: primary,
									borderColor: primary,
									fontFamily: 'Times New Roman',
									fontSize: '24px',
									fontWeight: 'bold',
									color: '#fff',
									border: 'none',
									borderRadius: 4,
									cursor: 'pointer',
								}}
							>
								{mod.defaultLabel}
							</button>
						</Col>
					)
				})}
			</Row>
		</Card>
	)

	return (
		<div style={{ paddingBottom: 80 }}>
			{edcHeader}
			{layout !== 'sidebar' ? edcTiles : null}
			<SidebarAttendanceOverview primary={primary} layout={layout} />

			{layout !== 'sidebar' ? (
				<>
					<CustomizeButton onClick={() => setCustomizeOpen(true)} />
					<WelcomeCustomizeDrawer
						open={customizeOpen}
						onClose={() => setCustomizeOpen(false)}
						modules={MODULE_DEFS}
						moduleIcons={moduleIcons}
						onIconsChange={setModuleIcons}
						layout={layout}
						onLayoutChange={saveLayout}
					/>
				</>
			) : null}
		</div>
	)
}

function Welcome() {
	const { theme } = useTheme()

	return (
		<>
			<GlobalHeader />
			{theme === 'primersoft' ? <PrimersoftWelcome /> : <EDCWelcome />}
		</>
	)
}

export default Welcome
